"use client"

import { CampaignPointsEarnedButton } from "@/components/Points/CampaignButton"
import PageContainer from "@/components/Points/PageContainer"
import { colors } from "@/components/Providers/ThemeRegistry/theme"
import {
  FluxCampaignIcon,
  GalxeCampaignIcon,
  OndoCampaignIcon,
  OusgCampaignIcon,
  UsdyCampaignIcon,
  WastedInterestCampaignIcon,
} from "@/components/svgs/Svgs"
import useWaveOneAddressPointsAttribution from "@/hooks/queryHooks/useWaveOneAddressPointsAttribution"
import { Button, Typography, alpha } from "@mui/material"
import Box from "@/components/Box"
import { BoosterChip, CampaignCard, NetworkChip } from "./CampaignCard"
import usePointsClaimed from "@/hooks/queryHooks/usePointsClaimed"

import usePointsLastUpdated from "@/hooks/queryHooks/usePointsLastUpdated"
import { useWallet } from "../Providers/WalletProvider"
import { MusdCampaignIcon } from "../svgs/musdCampaignIcon"

const CampaignsList = () => {
  const { address } = useWallet()

  const { data: addressPointsAttribution } = useWaveOneAddressPointsAttribution()
  const { data: { lastUpdatedAt, nextUpdate } = { lastUpdatedAt: "-", nextUpdate: "-" } } = usePointsLastUpdated()
  const { data: pointsClaimed } = usePointsClaimed(address)
  const hasDoneWastedStablecoin =
    pointsClaimed?.wastedStablecoin.signatureVerifiedAt !== null &&
    (addressPointsAttribution !== null || pointsClaimed?.wastedStablecoin.points == "0")
  const wastedStablecoinPoints = pointsClaimed?.wastedStablecoin.points ?? "0"

  return (
    <PageContainer sx={{ pt: "16px", pb: { xs: "92px", sm: "160px" }, position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          transform: "translate(-50%)",
          top: 0,
          width: "1200px",
          height: "1200px",
          willChange: "transform",
          borderRadius: "1200px",
          zIndex: -1,
          background: "#E2EBF8",
          filter: "blur(200px)",
        }}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "min(100%,327px)", sm: "repeat(auto-fill, min(100%,372px))" },
          gap: "24px",
          justifyContent: "center",
          maxWidth: "1200px",
          width: "100%",
        }}
      >
        <CampaignCard
          title="Wasted Stablecoin Interest"
          href="/points/campaigns/wasted-stablecoin"
          body="Claim Ondo Points for holding non yield-bearing USDC or USDT on Ethereum during 2023."
          cardIcon={<WastedInterestCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
            </>
          }
          button={
            <>
              {pointsClaimed?.wastedStablecoin.signatureVerifiedAt === null && (
                <Button variant="contained" fullWidth>
                  Claim Points
                </Button>
              )}
              {pointsClaimed?.wastedStablecoin.signatureVerifiedAt !== null &&
                addressPointsAttribution === null &&
                parseFloat(pointsClaimed?.wastedStablecoin.points ?? "0") > 0 && <ClaimedPointsDisplayedSoonButton />}
              {hasDoneWastedStablecoin && <CampaignPointsEarnedButton pointsEarned={wastedStablecoinPoints} />}
            </>
          }
        />
        <CampaignCard
          title="USDY Liquidity Provider"
          href="/points/campaigns/usdy-liquidity-provider"
          body="Earn 2.25 Ondo Points per dollar of liquidity provided per year through eligible Automated Liquidity Managers."
          cardIcon={<UsdyCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
              <NetworkChip network="mantle" />
              <NetworkChip network="solana" />
            </>
          }
          button={<CampaignPointsEarnedButton pointsEarned={addressPointsAttribution?.totalUsdyLpPoints} />}
        />
        <CampaignCard
          title="USDY Holder"
          href="/points/campaigns/usdy-holder"
          body="Earn 0.75 Ondo Points per USDY held per year."
          cardIcon={<UsdyCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
              <NetworkChip network="mantle" />
              <NetworkChip network="solana" />
            </>
          }
          button={<CampaignPointsEarnedButton pointsEarned={addressPointsAttribution?.usdyHolderPoints} />}
        />
        <CampaignCard
          title="Flux User"
          href="/points/campaigns/flux-user"
          body="Earn 0.75 Ondo Points per dollar deposited into Flux per year."
          cardIcon={<FluxCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
              <BoosterChip boosters={3} />
            </>
          }
          button={<CampaignPointsEarnedButton pointsEarned={addressPointsAttribution?.ftokenPointsWb} />}
        />
        <CampaignCard
          title="OUSG Holder"
          href="/points/campaigns/ousg-holder"
          body="Earn 3.75 Ondo Points per OUSG held per year."
          cardIcon={<OusgCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
            </>
          }
          button={<CampaignPointsEarnedButton pointsEarned={addressPointsAttribution?.ousgPoints} />}
        />
        <CampaignCard
          title="mUSD Holder"
          href="/points/campaigns/musd-holder"
          body="Earn 0.75 points per dollar worth of mUSD held per year."
          cardIcon={<MusdCampaignIcon />}
          chips={
            <>
              <NetworkChip network="mantle" />
            </>
          }
          button={<CampaignPointsEarnedButton pointsEarned={addressPointsAttribution?.musdHolderPoints} />}
        />
        <CampaignCard
          title="ONDO Holder"
          href=""
          body="Reward rate to be announced soon."
          cardIcon={<OndoCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
            </>
          }
          button={<PointsDisplayedSoonButton />}
        />

        <CampaignCard
          title="USDY Minter"
          href=""
          // body="Earn 2 Ondo Points per minted USDY, up to $500k."
          body="Reward rate to be announced soon."
          cardIcon={<UsdyCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
            </>
          }
          button={<PointsDisplayedSoonButton />}
        />
        <CampaignCard
          title="Galxe Campaigns"
          href=""
          // body="Earn 100 Ondo Points per Ondo Foundation sanctioned quest."
          body="Reward rate to be announced soon."
          cardIcon={<GalxeCampaignIcon />}
          button={<PointsDisplayedSoonButton />}
        />
        <CampaignCard
          title="ONDO Delegate"
          href=""
          // body="Delegates earn 0.1 Ondo Point per ONDO delegated to them per year."
          body="Reward rate to be announced soon."
          cardIcon={<OndoCampaignIcon />}
          chips={
            <>
              <NetworkChip network="ethereum" />
            </>
          }
          button={<PointsDisplayedSoonButton />}
        />
      </Box>
      <Typography
        variant="body1"
        color={alpha(colors.base.black, 0.5)}
        textAlign="center"
        mt={{ xs: "64px", sm: "80px" }}
      >
        Points are updated on a minimum weekly basis.
        <br />
        {lastUpdatedAt && (
          <>
            Last updated: {lastUpdatedAt}
            <br />
            Next update: {nextUpdate}
          </>
        )}
      </Typography>
    </PageContainer>
  )
}

export default CampaignsList

const PointsDisplayedSoonButton = () => (
  <Box
    sx={{
      display: "flex",
      padding: "8px 16px",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
    }}
  >
    <Typography color={alpha(colors.base.black, 0.4)}>Points will be displayed soon</Typography>
  </Box>
)

const ClaimedPointsDisplayedSoonButton = () => (
  <Box
    sx={{
      display: "flex",
      padding: "8px 16px",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
      textAlign: "center",
    }}
  >
    <Typography color={alpha(colors.base.black, 0.4)}>
      Your claimed points will appear after the next points update
    </Typography>
  </Box>
)
